const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Make authenticated request with JWT token
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
};

export const accountsApi = {
  /**
   * Fetch all accounts from the backend API
   * @returns {Promise<Array>} Array of account objects
   */
  getAllAccounts: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/accounts/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error;
    }
  },

  /**
   * Create a new account
   * @param {Object} accountData - Account data to create
   * @returns {Promise<Object>} Created account object
   */
  createAccount: async (accountData) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/accounts/`, {
        method: 'POST',
        body: JSON.stringify(accountData),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  },

  /**
   * Update an existing account
   * @param {number} accountId - Account ID to update
   * @param {Object} accountData - Updated account data
   * @returns {Promise<Object>} Updated account object
   */
  updateAccount: async (accountId, accountData) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/accounts/${accountId}`, {
        method: 'PUT',
        body: JSON.stringify(accountData),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  },

  /**
   * Delete an account
   * @param {number} accountId - Account ID to delete
   * @returns {Promise<Object>} Success message
   */
  deleteAccount: async (accountId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/accounts/${accountId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  },

  /**
   * Get users for KAM selection
   * @returns {Promise<Array>} Array of user objects
   */
  getUsersForKAMSelection: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/accounts/helpers/users`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching users for KAM selection:', error);
      throw error;
    }
  },

  /**
   * Toggle blacklist status for an account
   * @param {number} accountId - Account ID to toggle blacklist status
   * @param {Object} blacklistData - Optional reason and notes for blacklisting
   * @returns {Promise<Object>} Response with updated blacklist status
   */
  toggleBlacklist: async (accountId, blacklistData = {}) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/accounts/${accountId}/blacklist/toggle`, {
        method: 'POST',
        body: JSON.stringify(blacklistData),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling account blacklist:', error);
      throw error;
    }
  },

  /**
   * Get blacklist status and details for an account
   * @param {number} accountId - Account ID to check blacklist status
   * @returns {Promise<Object>} Blacklist status and details
   */
  getBlacklistStatus: async (accountId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/v1/accounts/${accountId}/blacklist`);

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching account blacklist status:', error);
      throw error;
    }
  }
};